// store global types
import type { TCreateSlice, TAsyncState } from "@/store";

// dependencies types
import type { TApiODIN, TPublicAuth0Config } from "@/apis/odin/apiODIN";

import {
    type TResponseGetInitialConfig,
    type TErrorGetInitialConfig,
} from "@/apis/odin/apiODIN";
import { createAsyncSlice } from "../createAsyncSlice";

type TStateInitialConfig = {
    initialConfigState: TAsyncState<
        TResponseGetInitialConfig,
        TErrorGetInitialConfig
    >;
};
type TActionsPublicInitialConfig = {
    initializeInitialConfig: () => Promise<void>;
    // Free-form env label, never branch on a specific value here — see
    // utils/getEnvironment.ts for capability helpers.
    getEnv: () => string | undefined;
};

export type TSliceInitialConfig = TStateInitialConfig &
    TActionsPublicInitialConfig;

export const initialStateInitialConfig = {
    initialConfigState: {
        status: "idle",
        data: null,
        error: null,
    },
} as const satisfies TStateInitialConfig;

export const createSliceInitialConfig: TCreateSlice<
    TSliceInitialConfig,
    {
        apiODIN: Pick<TApiODIN, "getInitialConfig">;
        initializeAuth: (authConfig: TPublicAuth0Config) => Promise<void>;
    }
> =
    ({ apiODIN, initializeAuth }) =>
    set => {
        const { initialConfigState, runInitialConfigState } = createAsyncSlice({
            name: "initialConfigState",
            asyncFn: async () => {
                const { data } = await apiODIN.getInitialConfig();
                const env = data.env;
                const authConfig = data.auth0Config;
                await initializeAuth(authConfig);
                return {
                    env,
                    auth0Config: authConfig,
                };
            },
        })(set);

        const getEnv = () => {
            return initialConfigState.data?.env;
        };

        return {
            initialConfigState,
            initializeInitialConfig: async () => {
                await runInitialConfigState();
            },
            getEnv,
        };
    };
